/**
 * Admin Dashboard — V2.N.7 (remplace le stub à stats simulées)
 *
 * Vue d'ensemble réelle agrégée depuis les endpoints admin :
 *   - Visiteurs (devices total / comptes / anonymes)
 *   - Wallets + portfolios suivis
 *   - Sessions actives (analytics 24h/7j/30j)
 *   - Derniers accès admin (audit log)
 *
 * Lit /api/admin/devices + /api/admin/analytics. Style Stripe light.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchAdminAction, fetchDevices, fetchAnalytics } from '../lib/fetchAdminAction';
import { relTime } from '../lib/dateHelpers';
import { adminPageStyle, adminCardStyle, adminTableStyle, adminThStyle, adminTdStyle, adminCodeStyle } from '../lib/adminStyles';
import '../layout/AdminLayout.css';

interface IdentityOverview {
  identities_total: number;
  identities_accounts: number;
  identities_anonymous: number;
  wallets_total: number;
  portfolios_total: number;
}
interface AnalyticsOverview {
  sessions_24h?: number;
  sessions_7d?: number;
  sessions_30d?: number;
  auth_users_24h?: number;
}
interface AuditRow {
  actor_email: string | null;
  auth_mode: string | null;
  endpoint: string;
  action: string | null;
  created_at: string;
}

export function Dashboard() {
  const [identities, setIdentities] = useState<IdentityOverview | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsOverview | null>(null);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [i, a, au] = await Promise.all([
        fetchAdminAction<IdentityOverview>('/api/admin/identities', 'overview'),
        fetchAnalytics<AnalyticsOverview>('overview', { days: 7 }),
        fetchDevices<AuditRow[]>('audit_log'),
      ]);
      if (!cancelled) {
        setIdentities(i);
        setAnalytics(a);
        setAudit(au || []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <main style={adminPageStyle}>
      <header style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Tableau de bord</h1>
        <div style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '4px' }}>
          Vue d'ensemble de la plateforme — données live.
        </div>
      </header>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        <StatCard to="/admin/users" label="Utilisateurs" value={identities?.identities_total ?? '—'} accent="#0969da" sub="Total (comptes + anonymes)" />
        <StatCard to="/admin/users" label="Comptes" value={identities?.identities_accounts ?? '—'} accent="#0e9f6e" sub="Inscrits sur la plateforme" />
        <StatCard to="/admin/users" label="Anonymes" value={identities?.identities_anonymous ?? '—'} accent="#d97706" sub="Identifiés par wallets" />
        <StatCard to="/admin/wallets" label="Wallets distincts" value={identities?.wallets_total ?? '—'} accent="#6366f1" />
        <StatCard to="/admin/portfolios" label="Portfolios" value={identities?.portfolios_total ?? '—'} accent="#8b5cf6" />
        <StatCard to="/admin/analytics" label="Sessions 7j" value={analytics?.sessions_7d ?? '—'} accent="#0e9f6e" sub={`${analytics?.auth_users_24h ?? 0} auth 24h`} />
      </div>

      <section style={adminCardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h2 style={{ fontSize: '15px', fontWeight: 700, margin: 0 }}>Derniers accès admin</h2>
          <span style={{ fontSize: '11px', color: 'var(--muted)' }}>Audit trail</span>
        </div>
        {loading ? (
          <div style={{ fontSize: '13px', color: 'var(--muted)' }}>Chargement…</div>
        ) : audit.length === 0 ? (
          <div style={{ fontSize: '12px', color: 'var(--muted)', fontStyle: 'italic' }}>Aucun accès enregistré pour le moment.</div>
        ) : (
          <table style={adminTableStyle}>
            <thead><tr><th style={adminThStyle}>Quand</th><th style={adminThStyle}>Admin</th><th style={adminThStyle}>Mode</th><th style={adminThStyle}>Endpoint</th><th style={adminThStyle}>Action</th></tr></thead>
            <tbody>
              {audit.slice(0, 20).map((r, i) => (
                <tr key={i}>
                  <td style={adminTdStyle}>{relTime(r.created_at)}</td>
                  <td style={adminTdStyle}>{r.actor_email || <span style={{ color: 'var(--muted)' }}>legacy key</span>}</td>
                  <td style={adminTdStyle}>
                    <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: r.auth_mode === 'jwt' ? '#0e9f6e' : '#d97706' }}>{r.auth_mode}</span>
                  </td>
                  <td style={adminTdStyle}><code style={adminCodeStyle}>{r.endpoint}</code></td>
                  <td style={adminTdStyle}>{r.action || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
}

function StatCard({ to, label, value, sub, accent }: { to: string; label: string; value: number | string; sub?: string; accent: string }) {
  return (
    <Link to={to} style={{ textDecoration: 'none' }}>
      <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px' }}>
        <div style={{ fontSize: '11px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: '26px', fontWeight: 700, marginTop: '4px', color: accent, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
        {sub && <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>{sub}</div>}
      </div>
    </Link>
  );
}

